import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeService from '../services/employeeService';
import { UserIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const EmployeeLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Real employee credentials from database
  const sampleCredentials = [
    { email: 'shubham@gmail.com', password: '123', name: 'Tushar' },
    { email: 'omkar@gmail.com', password: '123', name: 'Omkar' },
    { email: 'abc@gmail.com', password: '123', name: 'ABC' },
    { email: 'sai@gmail.com', password: '123', name: 'Sai' },
    { email: 'sushant@gmail.com', password: '123', name: 'Sushant' },
    { email: 'employee@example.com', password: '123', name: 'Employee Full Name' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await employeeService.login(formData.email, formData.password);
      
      if (result.success) {
        console.log('✅ Employee login successful, redirecting to dashboard...');
        
        // Get employee data from localStorage
        const employeeData = JSON.parse(localStorage.getItem('employeeData'));
        console.log('👤 Employee Data:', employeeData);
        console.log('🏢 Team Type:', employeeData.teamType);
        
        // Always redirect to unified dashboard - it will show the correct dashboard based on teamType
        console.log('🎯 Redirecting to Unified Dashboard');
        navigate('/unified-dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Employee login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSampleLogin = (credentials) => {
    setFormData({
      email: credentials.email,
      password: credentials.password
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
            <UserIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Employee Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your dashboard based on your team
          </p>
          
          {/* Team Info */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-blue-900 mb-2">📋 Dashboard Access by Team:</p>
            <div className="space-y-1 text-xs text-blue-800">
              <div className="flex items-center gap-2">
                <span className="font-medium">💼 Sales Team</span>
                <span className="text-blue-600">→ Sales Dashboard</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">📋 Reservation Team</span>
                <span className="text-blue-600">→ Reservation Dashboard</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">🎫 Other Services Team</span>
                <span className="text-blue-600">→ Other Services Dashboard</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">💰 Accounts Team</span>
                <span className="text-blue-600">→ Accounts Dashboard</span>
              </div>
            </div>
            <p className="text-xs text-blue-700 mt-2 italic">
              ℹ️ You'll be automatically redirected to your team's dashboard after login
            </p>
          </div>
        </div>

        {/* Sample Credentials */}
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Sample Employee Accounts:</h3>
          <div className="grid grid-cols-2 gap-2">
            {sampleCredentials.map((cred, index) => (
              <button
                key={index}
                onClick={() => handleSampleLogin(cred)}
                className="text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 rounded border transition-colors"
              >
                <div className="font-medium text-gray-900">{cred.name}</div>
                <div className="text-gray-600">{cred.email}</div>
              </button>
            ))}
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Sign in to Dashboard'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/main-login')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Back to Main Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeLogin;
